import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useOrganization } from "./OrganizationContext";

const PLAN_ORDER = ["FREE", "BASIC", "PRO"];
const PLAN_META = {
  FREE: { name: "Free", tint: "#F1F4F8", ink: "#3D4C60", dot: "#8A97A8" },
  BASIC: { name: "Basic", tint: "#EAF2FB", ink: "#1B4F82", dot: "#1F6FEB" },
  PRO: { name: "Pro", tint: "#EFEDFB", ink: "#4A3FBF", dot: "#6C5CE7" },
};
const FEATURE_LABELS = { LEAD: "Leads, reminders & inventory", LEAD_STRUCTURE: "Lead structure", LEAD_CUSTOM_FIELD: "Custom fields" };
const featureLabel = (k) => FEATURE_LABELS[k] || String(k || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
const rank = (p) => PLAN_ORDER.indexOf(String(p || "FREE").toUpperCase());
const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
// Superadmin Limits table → { PLAN: { leadLimit, projectLimit, monthlyPrice, yearlyPrice } }
const fetchPlanLimits = async (token) => {
  const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/subscription-limit`, authHeaders(token));
  const out = {};
  (Array.isArray(res.data) ? res.data : []).forEach((l) => { out[l.type] = l; });
  return out;
};
// Superadmin Features table → { PLAN: [featureKey] }, cascaded PRO ⊇ BASIC ⊇ FREE (same rule as fetchFeatures.js)
const fetchPlanMatrix = async (token) => {
  const rows = await Promise.all(PLAN_ORDER.map((p) =>
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/subscription-modal?type=${p}`, authHeaders(token))
      .then((r) => (Array.isArray(r.data) ? r.data.map((f) => f.feature) : [])).catch(() => [])));
  const out = {}; const acc = [];
  PLAN_ORDER.forEach((p, i) => { rows[i].forEach((k) => { if (!acc.includes(k)) acc.push(k); }); out[p] = [...acc]; });
  return out;
};

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";

// Plan comparison → "Continue to payment" → /dashboard/checkout?plan=…
// Everything shown (prices, limits, features) comes from the superadmin dashboard.
// Props: open, onClose, reason (headline), featureKey (highlights the plan that unlocks it)
const PlanUpgradeDialog = ({ open, onClose, reason, featureKey }) => {
  const navigate = useNavigate();
  const { role } = useOrganization();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const subs = String(JSON.parse(localStorage.getItem("subs")) || "FREE").toUpperCase();
  const [matrix, setMatrix] = useState(null);
  const [limits, setLimits] = useState(null);
  const canBuy = role === "ADMIN";
  const loading = !matrix || !limits;

  useEffect(() => {
    if (!open || !storedUser?.accessToken) return;
    fetchPlanMatrix(storedUser.accessToken).then(setMatrix).catch(() => setMatrix({}));
    fetchPlanLimits(storedUser.accessToken).then(setLimits).catch(() => setLimits({}));
  }, [open]);

  const featureKeys = useMemo(() => {
    const all = [];
    PLAN_ORDER.forEach((p) => (matrix?.[p] || []).forEach((k) => { if (!all.includes(k)) all.push(k); }));
    if (featureKey && !all.includes(featureKey)) all.push(featureKey);
    return all;
  }, [matrix, featureKey]);

  const plans = PLAN_ORDER.map((key) => {
    const m = PLAN_META[key];
    const lim = limits?.[key] || {};
    const price = Number(lim.monthlyPrice) || 0;
    const isCurrent = key === subs;
    const below = rank(key) <= rank(subs);
    const purchasable = key !== "FREE" && price > 0;
    const unlocks = !!featureKey && (matrix?.[key] || []).includes(featureKey) && !below;
    return {
      key, m, isCurrent, below, unlocks, purchasable,
      priceText: key === "FREE" ? "₹0" : price > 0 ? formatINR(price) : "—",
      perText: key === "FREE" ? "forever" : price > 0 ? "per month" : "price not set",
      badge: isCurrent ? "Current plan" : below ? "Downgrade" : unlocks ? "Recommended" : "Upgrade",
      limits: [
        { label: "Leads", value: lim.leadLimit != null ? Number(lim.leadLimit).toLocaleString("en-IN") : "—" },
        { label: "Projects", value: lim.projectLimit != null ? String(lim.projectLimit) : "—" },
      ],
      features: featureKeys.map((k) => ({
        key: k,
        label: featureLabel(k),
        included: (matrix?.[key] || []).includes(k),
        highlight: k === featureKey,
      })),
    };
  });

  const choose = (plan) => {
    onClose?.();
    navigate(`/dashboard/checkout?plan=${plan}${featureKey ? `&feature=${featureKey}` : ""}`);
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 880, maxWidth: "calc(100% - 32px)", borderRadius: "18px", boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)", fontFamily: FONT, color: INK } }}
      slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "20px 22px 16px", borderBottom: "1px solid #EDF1F5" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: "#4A3FBF" }}>Upgrade</div>
          <h2 style={{ margin: "3px 0 0", fontFamily: FONT, fontSize: 22, fontWeight: 800, letterSpacing: "-0.028em", textWrap: "pretty" }}>
            {reason || "This is part of a paid plan."}
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "#4A5A6E", textWrap: "pretty" }}>
            Your leads, users and settings carry over. Change or cancel any time.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, background: "#F4F7FA", border: "1px solid #E4E9EF", borderRadius: 9, fontSize: 15, lineHeight: 1, color: "#4A5A6E", cursor: "pointer" }}
        >×</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, padding: "18px 22px 4px", opacity: loading ? 0.5 : 1, transition: "opacity .2s" }}>
        {plans.map((p) => (
          <div
            key={p.key}
            style={{
              display: "flex", flexDirection: "column", padding: "15px 15px 16px", borderRadius: 14,
              background: p.isCurrent ? p.m.tint : "#FFFFFF",
              border: `1.5px solid ${p.isCurrent || p.unlocks ? p.m.dot : "#E4E9EF"}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em" }}>{p.m.name}</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: p.isCurrent || p.unlocks ? p.m.ink : "#66748A" }}>{p.badge}</span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 6 }}>
              <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.035em" }}>{p.priceText}</span>
              <span style={{ fontSize: 12.5, color: "#66748A" }}>{p.perText}</span>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, padding: "9px 11px", background: "#FFFFFF", border: "1px solid #E4E9EF", borderRadius: 10 }}>
              {p.limits.map((l) => (
                <div key={l.label} style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#66748A" }}>{l.label}</div>
                  <div style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.value}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12 }}>
              {p.features.map((f) => (
                <div key={f.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0" }}>
                  <span style={{ flex: "0 0 auto", fontSize: 12, fontWeight: 800, color: f.included ? "#1B6B41" : "#B4BFCC" }}>{f.included ? "✓" : "·"}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: f.included ? INK : "#8A97A8", fontWeight: f.highlight && f.included ? 700 : 400, textWrap: "pretty" }}>{f.label}</span>
                </div>
              ))}
            </div>

            <div style={{ flex: 1, minHeight: 10 }} />
            {p.isCurrent ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 38, marginTop: 12, background: "#FFFFFF", border: "1px solid #E4E9EF", borderRadius: 9, fontSize: 13.5, fontWeight: 700, color: "#66748A" }}>
                You're on this plan
              </div>
            ) : !p.below ? (
              <button
                type="button"
                disabled={canBuy && !p.purchasable}
                onClick={() => (canBuy ? choose(p.key) : onClose?.())}
                style={{ width: "100%", height: 38, marginTop: 12, background: canBuy && !p.purchasable ? "#B4BFCC" : p.unlocks ? "#4A3FBF" : "#0B5C8A", color: "#FFFFFF", border: 0, borderRadius: 9, fontFamily: FONT, fontSize: 13.5, fontWeight: 700, cursor: canBuy && !p.purchasable ? "not-allowed" : "pointer" }}
              >
                {canBuy ? (p.purchasable ? "Continue to payment" : "Not available yet") : "Ask your admin"}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, padding: "14px 22px 20px" }}>
        <span style={{ flex: "1 1 220px", fontSize: 12.5, color: "#66748A", textWrap: "pretty" }}>
          {canBuy
            ? "You'll be handed to our payment provider. Your data and users stay as they are."
            : "Only your organisation's admin can change the plan."}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{ flex: "0 0 auto", height: 36, padding: "0 15px", background: "#FFFFFF", border: "1px solid #D5DEE7", borderRadius: 9, fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: INK, cursor: "pointer" }}
        >Not now</button>
      </div>
    </Dialog>
  );
};

export default PlanUpgradeDialog;
